import { useEffect, useState } from 'react';
import { getDownloadsHash, postDownload } from '~/api';
import { useNameFromSearch } from '~/hooks/useNameFromSearch';
import { useUser } from '~/hooks/useUser';
import useSampleDownload from '~/hooks/useSampleDownload';
import { useHandleException } from '~/scripts/handleException';
import { is402Status } from '~/scripts/network';
import useDataLayer from '~/services/datalayer';
import type { Filters } from '~/types/filters';
import type { Searches } from '~/types/searches';
interface downloadSearchProps {
  search: Searches.SearchObject | undefined;
  columns: Record<string, string> | undefined;
  recaptchaToken: string | undefined;
  total: number;
  rows: number | undefined;
  filters: Filters.Filters;
  uuid: string | undefined;
  requiresDownloadRecaptcha: boolean;
  isDownloading: boolean;
  setIsDownloading: (isDownloading: boolean) => void;
  setRecaptchaOpen: (open: boolean) => void;
}

export default function useDownloadFile({
  search,
  columns,
  recaptchaToken,
  requiresDownloadRecaptcha,
  total,
  rows,
  filters,
  uuid,
  isDownloading,
  setIsDownloading,
  setRecaptchaOpen,
}: downloadSearchProps) {
  const user = useUser();
  const [file, setFile] = useState<string>('');
  const [hash, setHash] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [requestCount, setRequestCount] = useState(0);
  const handleException = useHandleException();
  const dataLayer = useDataLayer();
  const name = useNameFromSearch(search);
  const downloadSampleReport = useSampleDownload(uuid, name, total);

  // clear out file when starting a new download
  useEffect(() => {
    setFile('');
    setHash('');
  }, [isDownloading]);

  // make initial POST request
  useEffect(() => {
    if (!isDownloading || !search || !columns || !rows) {
      return;
    }
    setFile('');
    setError('');
    setHash('');

    if (requiresDownloadRecaptcha && !recaptchaToken) {
      setRecaptchaOpen(true);
      return;
    }

    dataLayer.push({
      event: 'POST /downloads',
    });
    postDownload({
      user: user.uuid || '',
      search,
      columns,
      total,
      rows,
      filters,
      re: recaptchaToken,
    })
      .then(({ hash, error }) => {
        if (error) {
          return setError(error);
        }
        setHash(hash);

        dataLayer.push({
          event: 'download',
          type: 'search',
          query: name,
          total: total || 0,
          uuid: uuid,
        });
      })
      .catch((error) => {
        setIsDownloading(false);
        if (is402Status(error)) {
          downloadSampleReport();
        } else {
          handleException(error, {
            category: 'report download',
          });
        }
      });
  }, [
    isDownloading,
    search,
    columns,
    recaptchaToken,
    rows,
    filters,
    name,
    user.uuid,
    uuid,
    requiresDownloadRecaptcha,
    setRecaptchaOpen,
    total,
    dataLayer,
    downloadSampleReport,
    handleException,
    setIsDownloading,
  ]);

  // make GET request recursively until file is ready
  useEffect(() => {
    if (!hash || !isDownloading) {
      return;
    }
    dataLayer.push({
      event: 'GET /downloads/:hash',
    });

    let retryTimeout: ReturnType<typeof setTimeout> | undefined;
    getDownloadsHash(hash).then(({ file, error }) => {
      if (error) {
        setIsDownloading(false);
        return setError(error);
      }
      if (file) {
        setFile(file);
      } else {
        retryTimeout = setTimeout(() => {
          setRequestCount(requestCount + 1);
        }, 3000);
      }
    });

    return () => {
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
    };
  }, [hash, requestCount, setIsDownloading, isDownloading, dataLayer]);

  return {
    file,
    hash,
    error,
  };
}
